import { OutlinedInput, OutlinedInputProps, styled, SxProps } from "@mui/material";
import { t } from "i18next";
import { useEffect, useState } from "react";

export type inputTimecodeStateType = "error" | "warning" | undefined;

interface InputTimecodeProps extends OutlinedInputProps {
  value: string;
  frameRate: number;
  status?: inputTimecodeStateType;
  handleKeyDown(event: any): void;
  setValue(newTimecode: string): void;
  size?: "small" | "large" | "medium" | "xlarge" | undefined;
}

function reverseString(str) {
  let splitString = str.split(""); // let splitString = "hello".split("");
  let reverseArray = splitString.reverse(); // let reverseArray = ["h", "e", "l", "l", "o"].reverse();
  let joinArray = reverseArray.join(""); // let joinArray = ["o", "l", "l", "e", "h"].join("");
  return joinArray; // "olleh"
}

const checkTimecodeByLength = [
  (str: string, charSet: string): boolean => {
    return new RegExp("^$").test(str);
  },
  (str: string, charSet: string): boolean => {
    return new RegExp("^([012])$").test(str);
  },
  (str: string, charSet: string): boolean => {
    return new RegExp("^((?:[01]\\d|2[0123]))$").test(str);
  },
  (str: string, charSet: string): boolean => {
    return new RegExp("^((?:[01]\\d|2[0123])[" + charSet + "])$").test(str);
  },
  (str: string, charSet: string): boolean => {
    return new RegExp("^((?:[01]\\d|2[0123])[" + charSet + "](?:[012345]))$").test(str);
  },
  (str: string, charSet: string): boolean => {
    return new RegExp("^((?:[01]\\d|2[0123])[" + charSet + "](?:[012345]\\d))$").test(str);
  },
  (str: string, charSet: string): boolean => {
    return new RegExp("^((?:[01]\\d|2[0123])[" + charSet + "](?:[012345]\\d)[" + charSet + "])$").test(str);
  },
  (str: string, charSet: string): boolean => {
    return new RegExp("^((?:[01]\\d|2[0123])[" + charSet + "](?:[012345]\\d)[" + charSet + "](?:[012345]))$").test(str);
  },
  (str: string, charSet: string): boolean => {
    return new RegExp("^((?:[01]\\d|2[0123])[" + charSet + "](?:[012345]\\d)[" + charSet + "](?:[012345]\\d))$").test(
      str
    );
  },
  (str: string, charSet: string): boolean => {
    return new RegExp(
      "^((?:[01]\\d|2[0123])[" + charSet + "](?:[012345]\\d)[" + charSet + "](?:[012345]\\d)[" + charSet + "])$"
    ).test(str);
  },
  (str: string, charSet: string): boolean => {
    return new RegExp(
      "^((?:[01]\\d|2[0123])[" +
        charSet +
        "](?:[012345]\\d)[" +
        charSet +
        "](?:[012345]\\d)[" +
        charSet +
        "](?:[012]))$"
    ).test(str);
  },
  (str: string, charSet: string): boolean => {
    return new RegExp(
      "^((?:[01]\\d|2[0123])[" +
        charSet +
        "](?:[012345]\\d)[" +
        charSet +
        "](?:[012345]\\d)[" +
        charSet +
        "](?:[012]\\d))$"
    ).test(str);
  },
];

const InputTimecode: React.FC<InputTimecodeProps> = (props) => {
  const [error, SetError] = useState<boolean>(false); //!checkTimecode(timecodeSeek, frameRate));
  const { ...otherProps } = props;
  const charSet = props.frameRate === 29.97 || props.frameRate === 59.94 ? ":;" : ":";
  const regexString = new RegExp(
    "^((?:[01]\\d|2[0123])[" +
      charSet +
      "](?:[012345]\\d)[" +
      charSet +
      "](?:[012345]\\d)[" +
      charSet +
      "](?:[012]\\d))$|^((?:[01]\\d|2[0123])[" +
      charSet +
      "](?:[012345]\\d)[" +
      charSet +
      "](?:[012345]\\d)[" +
      charSet +
      "](?:[012]))$|^((?:[01]\\d|2[0123])[" +
      charSet +
      "](?:[012345]\\d)[" +
      charSet +
      "](?:[012345]\\d)[" +
      charSet +
      "])$|^((?:[01]\\d|2[0123])[" +
      charSet +
      "](?:[012345]\\d)[" +
      charSet +
      "](?:[012345]\\d))$|^((?:[01]\\d|2[0123])[" +
      charSet +
      "](?:[012345]\\d)[" +
      charSet +
      "](?:[012345]))$|^((?:[01]\\d|2[0123])[" +
      charSet +
      "](?:[012345]\\d)[" +
      charSet +
      "])$|^((?:[01]\\d|2[0123])[" +
      charSet +
      "](?:[012345]\\d))$|^((?:[01]\\d|2[0123])[" +
      charSet +
      "](?:[012345]))$|^((?:[01]\\d|2[0123])[" +
      charSet +
      "])$|^((?:[01]\\d|2[0123]))$|^([012])$|^$"
  );

  const findChangeIndex = (str1: string, str2: string) => {
    let i = 0;
    while (i < Math.min(str1.length, str2.length) && str1[i] === str2[i]) ++i;

    return i;
  };
  // cursorPositon (after char at index X of the new string)
  const findChangeLength = (str1: string, str2: string, cursorPositon: number): number => {
    let i = 0;
    while (i < Math.min(str1.length, str2.length) && str1[i] === str2[i]) ++i;

    let reversStr1 = reverseString(str1);
    let reversStr2 = reverseString(str2);
    let y = 0;
    while (y < Math.min(str1.length, str2.length) && reversStr1[y] === reversStr2[y]) ++y;

    let yy = y;
    if (i === cursorPositon) {
      return (
        (Math.max(str1.length, str2.length) - Math.min(str1.length, str2.length)) * (str1.length > str2.length ? 1 : -1)
      );
    }
    if (i > Math.min(str1.length, str2.length) - y) {
      return cursorPositon - i;
    }

    if (cursorPositon > Math.min(str1.length, str2.length)) {
      return cursorPositon - i;
    }
    if (cursorPositon < Math.min(str1.length, str2.length)) {
      return cursorPositon - i;
    }
    // (length -1 and Index +1 as we compute with index)
    if (yy - i != 0) return yy - i;

    if (str1 !== str2) return str1.length; // or str2, doesn't matter.

    return 0;
  };

  const OnChange = (event) => {
    let position: number = findChangeIndex(event.target.value, event.target.defaultValue);
    let curLength = event.target.value.length;
    let changeLength: number = findChangeLength(
      event.target.value,
      event.target.defaultValue,
      event.target.selectionStart
    );
    let isAtEnd: boolean = event.target.defaultValue.length === position;
    let beforeNewSection: string = event.target.value.substring(0, position);
    let newSection: string = event.target.value.substring(position, position + (changeLength < 0 ? 0 : changeLength));
    let afterNewSection: string = event.target.value.substring(position + (changeLength < 0 ? 0 : changeLength));

    // maybe useless
    if (changeLength === 0) return;
    // if (isAtEnd && changeLength === 1 && !regexString.test(event.target.value)) {
    //   SetError(true);
    //   return;
    // } else
    if (Math.abs(changeLength) > 1 && regexString.test(event.target.value)) {
      props.setValue(event.target.value);
      SetError(false);
      return;
    }
    // maybe useless

    let isWrittingAtSplitLength = (position === 2 || position === 5 || position === 8) && changeLength > 0;
    let writeSeparator = (curLength === 2 || curLength === 5 || curLength === 8) && changeLength > 0;
    let isStepCompleted: boolean = false;

    if (isWrittingAtSplitLength) {
      event.target.value = beforeNewSection;

      if (
        !isStepCompleted &&
        newSection.length > 0 &&
        !charSet.includes(beforeNewSection[beforeNewSection.length - 1]) &&
        !charSet.includes(newSection[0])
      ) {
        event.target.value = event.target.value + ":" + newSection;
        isStepCompleted = true;
      } else event.target.value = event.target.value + newSection;

      if (!checkTimecodeByLength[event.target.value.length](event.target.value, charSet)) {
        SetError(true);
        return;
      }

      if (
        !isStepCompleted &&
        afterNewSection.length > 0 &&
        !charSet.includes(newSection[newSection.length - 1]) &&
        !charSet.includes(afterNewSection[0])
      ) {
        event.target.value = event.target.value + ":" + afterNewSection;
        isStepCompleted = true;
      } else event.target.value = event.target.value + afterNewSection;
    }

    if (event.target.value.length >= 11 && !checkTimecodeByLength[11](event.target.value, charSet)) {
      SetError(true);
      return;
    } else if (
      !isStepCompleted &&
      changeLength > 0 &&
      !checkTimecodeByLength[position + (changeLength < 0 ? 0 : changeLength)](
        event.target.value.substring(0, position + (changeLength < 0 ? 0 : changeLength)),
        charSet
      )
    ) {
      SetError(true);
      return;
    }

    props.setValue(event.target.value + (writeSeparator ? ":" : ""));
    SetError(false);
  };

  useEffect(() => {
    if (props.status === "error") SetError(true);
    else SetError(false);
  }, [props.status]);

  return (
    <StyledOutlinedInput
      {...otherProps}
      label={t("common:goToTc")}
      value={props.value}
      placeholder="00:00:00:00"
      error={error}
      color="success"
      status={props.status}
      onKeyDown={props.handleKeyDown}
      onChange={OnChange}
      size={props.size}
    />
  );
};

const StyledOutlinedInput = styled(OutlinedInput, { shouldForwardProp: (propName) => propName !== "status" })<{
  status?: inputTimecodeStateType;
}>(({ theme, status, error }) => ({
  ...(!error && {
    "&:hover": {
      "& .MuiOutlinedInput-notchedOutline": {
        color: theme.palette.primary.text,
        borderBottomColor: status === "warning" ? theme.palette.warning.main : "",
      },
    },
    "&.Mui-focused": {
      "& .MuiOutlinedInput-notchedOutline": {
        color: theme.palette.primary.text,
        borderBottomColor: status === "warning" ? theme.palette.warning.main : "",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderBottomColor: status === "warning" ? theme.palette.warning.main : "",
    },
  }),
  color: theme.palette.text.primary,
}));

export default InputTimecode;
